import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { GraduationCap, Phone, ArrowRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { schoolInfo } from '../utils/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeacherLoginPage = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1: mobile, 2: OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [demoOtp, setDemoOtp] = useState('');

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate mobile number
    if (mobile.length !== 10 || !/^\d+$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number');
      setLoading(false);
      return;
    }

    try {
      // Check if teacher exists
      const checkResponse = await axios.get(`${BACKEND_URL}/api/teachers/check/${mobile}`);
      
      if (!checkResponse.data.exists) {
        setError('Mobile number not registered. Please contact admin.');
        toast.error('Not Registered', {
          description: 'Please contact school admin'
        });
        setLoading(false);
        return;
      }

      // Teacher exists, send OTP
      const otpResponse = await axios.post(`${BACKEND_URL}/api/otp/send-otp`, {
        mobile_number: mobile
      });

      if (otpResponse.data.status === 'success') {
        // Store teacher data for later use
        sessionStorage.setItem('teacherMobile', mobile);
        
        // Show OTP if demo mode
        if (otpResponse.data.otp) {
          setDemoOtp(otpResponse.data.otp);
          toast.success('OTP Generated', {
            description: `Your OTP: ${otpResponse.data.otp}`
          });
        }

        toast.success('OTP sent to your mobile');
        setStep(2);
        setLoading(false);
      } else {
        throw new Error('Failed to send OTP');
      }

    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to send OTP. Please try again.';
      setError(errorMsg);
      toast.error('Error', {
        description: errorMsg
      });
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate OTP
    if (otp.length !== 6 || !/^\d+$/.test(otp)) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      // Verify OTP
      const response = await axios.post(`${BACKEND_URL}/api/otp/verify-otp`, {
        mobile_number: mobile,
        otp: otp
      });

      if (response.data.valid) {
        // Get teacher data
        const teacherResponse = await axios.get(`${BACKEND_URL}/api/teachers/check/${mobile}`);
        const teacherData = teacherResponse.data.teacher;
        
        // Store teacher data
        localStorage.setItem('teacherData', JSON.stringify(teacherData));
        localStorage.setItem('isTeacherAuthenticated', 'true');

        toast.success('Login Successful!', {
          description: 'Welcome to Teacher Portal'
        });

        setTimeout(() => {
          navigate('/teacher-dashboard');
        }, 500);
      } else {
        setError('Invalid or expired OTP. Please try again.');
        setLoading(false);
      }

    } catch (err) {
      console.error('OTP verification error:', err);
      const errorMsg = err.response?.data?.detail || 'Invalid or expired OTP. Please try again.';
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 via-green-500 to-teal-600 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="bg-white rounded-full p-4 w-20 h-20 mx-auto mb-4 shadow-lg transform hover:scale-110 transition-transform duration-300">
            <img src={schoolInfo.logo} alt="School Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{schoolInfo.name}</h1>
          <p className="text-green-100 flex items-center justify-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Teacher Portal
          </p>
        </div>

        {/* Login Card */}
        <Card className="shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-6 bg-gradient-to-r from-green-50 to-white">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="h-6 w-6 text-green-600" />
              <CardTitle className="text-2xl font-bold text-center">Teacher Login</CardTitle>
            </div>
            <CardDescription className="text-center">
              {step === 1 ? 'Enter your mobile number to receive OTP' : 'Enter the OTP sent to your mobile'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              // Step 1: Mobile Number
              <form onSubmit={handleSendOTP} className="space-y-6">
                {error && (
                  <Alert variant="destructive" className="animate-shake">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label htmlFor="mobile" className="text-base">Mobile Number</Label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="mobile"
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter mobile number"
                      className="pl-10 py-6 text-lg"
                      maxLength="10"
                      autoComplete="tel"
                      name="mobile"
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter 10-digit mobile number (no need to add +91)
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full py-6 text-lg bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Send OTP
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              // Step 2: OTP Verification
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                {demoOtp && (
                  <Alert className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                    <AlertDescription className="text-center">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Testing Mode - OTP</p>
                      <p className="text-4xl font-bold text-blue-600 tracking-widest mb-2">
                        {demoOtp}
                      </p>
                      <p className="text-xs text-gray-600">
                        This OTP is shown for testing purposes only
                      </p>
                    </AlertDescription>
                  </Alert>
                )}

                {error && (
                  <Alert variant="destructive" className="animate-shake">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label htmlFor="otp" className="text-base">Enter OTP</Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    className="py-6 text-lg text-center tracking-widest"
                    maxLength="6"
                    required
                    autoFocus
                  />
                  <p className="mt-1 text-xs text-gray-500 text-center">
                    OTP sent to +91{mobile}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep(1);
                      setOtp('');
                      setError('');
                      setDemoOtp('');
                    }}
                    className="flex-1 py-6"
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 py-6 text-lg bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </>
                    ) : (
                      'Verify & Login'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-white text-sm">
          <p>Not a teacher?</p>
          <Button
            variant="link"
            onClick={() => navigate('/login')}
            className="text-white hover:text-green-200 underline"
          >
            Parent Login
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeacherLoginPage;
